import { resolveSafeBootScreen } from "./screenGuards";
import { resolveOrgPolicies } from "./permissions";
import { canRestoreCachedAuthentication } from "./connectivity";

export const CURRENT_USER_KEY = "CP_CURRENT_USER";
export const LAST_SCREEN_KEY = "CP_LAST_SCREEN";
export const DIRECT_REPORT_IDS_KEY = "CP_DIRECT_REPORT_IDS";

const BLOCKED_RESTORE_SCREENS = new Set([
  "onboarding",
  "email_confirmation",
  "update_password",
  "payroll_print_isolation",
]);

export function getLocalCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem(CURRENT_USER_KEY));
  } catch {
    return null;
  }
}

export function saveLocalCurrentUser(user) {
  localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
}

export function clearLocalCurrentUser() {
  localStorage.removeItem(CURRENT_USER_KEY);
  localStorage.removeItem(LAST_SCREEN_KEY);
  localStorage.removeItem(DIRECT_REPORT_IDS_KEY);
}

export function getLocalDirectReportIds() {
  try {
    const raw = localStorage.getItem(DIRECT_REPORT_IDS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function saveLocalDirectReportIds(ids) {
  if (!Array.isArray(ids)) {
    return;
  }
  localStorage.setItem(
    DIRECT_REPORT_IDS_KEY,
    JSON.stringify(ids.filter(Boolean)),
  );
}

export function clearLocalDirectReportIds() {
  localStorage.removeItem(DIRECT_REPORT_IDS_KEY);
}

export function getLastScreen() {
  try {
    return localStorage.getItem(LAST_SCREEN_KEY);
  } catch {
    return null;
  }
}

export function saveLastScreen(screen) {
  if (!screen || BLOCKED_RESTORE_SCREENS.has(screen)) {
    return;
  }
  localStorage.setItem(LAST_SCREEN_KEY, screen);
}

export function hasCachedAuthenticatedUser() {
  const user = getLocalCurrentUser();
  return Boolean(user && !user.isGuest && user.supabase_user_id);
}

export function hasRestorableOfflineSession() {
  return hasCachedAuthenticatedUser() && canRestoreCachedAuthentication();
}

/**
 * True while initial auth bootstrap should block UI (avoids onboarding flash).
 */
export function shouldShowAuthBootstrapLoading(hydrated) {
  return !hydrated?.currentUser;
}

export function applyCachedSessionRestore(cachedUser = getLocalCurrentUser()) {
  if (!cachedUser?.supabase_user_id) {
    return null;
  }

  return {
    currentUser: cachedUser,
    userName: cachedUser.employee_name || cachedUser.email || null,
    userEmail: cachedUser.email || null,
    session: buildOfflineSessionShim(cachedUser),
  };
}

export function buildOfflineSessionShim(cachedUser) {
  if (!cachedUser?.supabase_user_id) {
    return null;
  }

  return {
    user: {
      id: cachedUser.supabase_user_id,
      email: cachedUser.email || null,
    },
  };
}

/**
 * Supabase may return a session object without a hydrated user when offline.
 * Preserve user.id from the previous app session or CP_CURRENT_USER.
 */
export function normalizeAppSession(nextSession, previousSession = null) {
  if (nextSession?.user?.id) {
    return nextSession;
  }

  const cachedUser = getLocalCurrentUser();
  const fallbackUser =
    previousSession?.user?.id != null
      ? previousSession.user
      : buildOfflineSessionShim(cachedUser)?.user;

  if (!fallbackUser?.id) {
    return nextSession;
  }

  if (!nextSession) {
    return { user: fallbackUser };
  }

  return {
    ...nextSession,
    user: {
      ...fallbackUser,
      ...nextSession.user,
      id: fallbackUser.id,
    },
  };
}

function resolveBootScreen(cachedUser) {
  const lastScreen = getLastScreen();
  const policies = resolveOrgPolicies(
    cachedUser?.org_settings || cachedUser?.orgSettings || {},
  );
  return resolveSafeBootScreen(cachedUser, lastScreen, policies);
}

/**
 * Synchronous boot hydration from localStorage.
 * Never awaits network — safe to call during initial React state setup.
 */
export function hydrateOfflineBoot() {
  const cachedUser = getLocalCurrentUser();
  const canRestoreOffline = hasRestorableOfflineSession();

  if (!canRestoreOffline) {
    return {
      currentUser: null,
      screen: "onboarding",
      userName: null,
      userEmail: null,
      directReportIds: [],
      hasCachedUser: hasCachedAuthenticatedUser(),
    };
  }

  return {
    currentUser: cachedUser,
    screen: resolveBootScreen(cachedUser),
    userName: cachedUser.employee_name || cachedUser.email || null,
    userEmail: cachedUser.email || null,
    directReportIds: getLocalDirectReportIds(),
    hasCachedUser: true,
  };
}
